<template>
  <div class="flex-1 flex flex-col w-full overflow-auto gap-6">
    <div class="vstack items-center">
      <Avatar
        :avatar="user.avatar"
        class="w-90px h-90px"
      />
      <TextButton
        :label="_t('user.update_avatar')"
        :loading="avatarLoading"
        @click="onChangeAvatar"
      ></TextButton>
    </div>

    <div class="w-full">
      <ListCell
        :title="_t('user.nickname')"
        :label="user.nickname"
      >
        <template #action>
          <TextButton
            :label="_t('common.update')"
            :loading="nicknameLoading"
            @click="onChangeNickname"
          ></TextButton>
        </template>
      </ListCell>
      <ListCell
        :title="_t('user.lid')"
        :label="user.id"
      >
        <template #action>
          <TextButton
            :label="_t('common.copy')"
            @click="onCopyId"
          ></TextButton>
        </template>
      </ListCell>

      <ListCell
        :title="_t('user.account_bind')"
        arrow
        @click="emit('toAccount')"
      ></ListCell>
    </div>

    <div class="w-full">
      <ListCell
        :title="_t('user.preference')"
        arrow
        @click="emit('toOperationPreference')"
      />
      <ListCell
        v-if="showNotificationCell"
        :title="_t('user.notification')"
        arrow
        @click="emit('toNotification')"
      />
      <ListCell
        :title="_t('user.learn_stage')"
        :label="identityTags[0]?.name"
        :arrow="false"
        class="break-all"
      >
        <template #action>
          <TextButton
            :label="_t('common.update')"
            @click="onChangeIdentityTag"
          ></TextButton>
        </template>
      </ListCell>
      <ListCell :title="_t('user.clear_cache')">
        <template #action>
          <TextButton
            :label="_t('user.clear_immediately')"
            @click="onCacheClear"
          ></TextButton>
        </template>
      </ListCell>
    </div>

    <div
      v-if="showWidget"
      class="w-full"
    >
      <ListCell
        :title="_t('user.home_widget')"
        arrow
        @click="onClickHomeWidget"
      />
    </div>

    <div class="mt-auto flex flex-col gap-16px">
      <Button
        :label="_t('user.logout')"
        scene="danger"
        @click="onLogout"
      />

      <div class="text-13px text-ld-label-secondary text-center">
        {{ _t('common.version', { version: gitHash }) }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useCommonStore } from '@/stores/common-store'
import { fetchUserTags, patchUser, uploadAvatar } from '@/api/user'

import { useRouter } from 'vue-router'
import ListCell from '@/components/ListCell/ListCell.vue'
import { openLink, pickImg } from '@/utils/misc'
import { Code } from '@/api/code'

import db, { mergeDefaultDb, type DB } from '@/db'
import type { IdentityTag } from '@/types/core'
import { useCopy } from '@/hooks'
import { checkNotificationAvaiable } from '@/utils/notification'
import { Platform } from '@/utils/bridge'

const router = useRouter()
const store = useCommonStore()
const user = store.user!

const emit = defineEmits<{
  toAccount: []
  toOperationPreference: []
  toNotification: []
  done: []
}>()

const gitHash = computed(() => _global.gitCommit.split('(')[0])

// 是否显示小组件
const showWidget = ref(false)

onInit(() => {
  if (_global.isInsideApp) {
    _bridge.has('homewidget').then(res => {
      showWidget.value = res
    })
  }
})

function onLogout() {
  store.logout()
  router.push({ name: 'landing' })
  emit('done')
}

const nicknameLoading = ref(false)
const copy = useCopy()

function onClickHomeWidget() {
  if (store.appInfo?.platform === Platform.IOS) {
    openLink(_global.iosWidgetLink)
  } else {
    openLink(_global.androidWidgetLink)
  }
}

function onChangeNickname() {
  _openInputDialog({
    title: _t('user.input_new_nickname'),
    placeholder: _t('user.input_new_nickname'),
    okText: _t('common.save'),
    async onSubmit(newNickname: string) {
      if (newNickname == null) return false

      if (newNickname.length < 2) {
        _message.info(_t('user.nickname_validate_msg1', { count: 2 }))
        return false
      }
      if (newNickname.length > 10) {
        _message.info(_t('user.nickname_validate_msg1', { count: 10 }))
        return false
      }
      if (!/^[^!\"#$%&'()*+,./:;<=>?@[\\\]^`{|}~]*$/.test(newNickname)) {
        _message.info(_t('user.nickname_validate_msg3'))
        return false
      }

      nicknameLoading.value = true
      await patchUser({ nickname: newNickname.trim() })
        .then(() => {
          user.nickname = newNickname
          _message.success(_t('common.update_success'))
        })
        .finally(() => {
          nicknameLoading.value = false
        })
      return true
    },
  })
}

function onCopyId() {
  copy(user.id).then(() => {
    _message.success(_t('common.copied'))
  })
}

const avatarLoading = ref(false)

async function onChangeAvatar() {
  const imgFile = await pickImg(_t('user.update_avatar'), true)

  if (!imgFile) return

  avatarLoading.value = true
  try {
    const res = await uploadAvatar(imgFile)

    if (res.code !== Code.Ok) {
      _message.info(res.message)
    } else {
      store.patchUser({ avatar: res.data.assetId })
      _message.success(_t('user.update_avatar_success'))
    }
  } finally {
    avatarLoading.value = false
  }
}

// 需要清除的 db 缓存 keys
const CLEARABLE_DB_KEYS: (keyof DB)[] = ['packageState']
function onCacheClear() {
  // 清除 DB 缓存
  const newDb = mergeDefaultDb({
    loginToken: db.loginToken,
  })

  for (const key of CLEARABLE_DB_KEYS) {
    ;(db as any)[key] = newDb[key as keyof DB]
  }

  // 清除资源缓存
  _am.clearAllCache()

  _message.info('缓存已全部清理')
}

const identityTags = ref<IdentityTag[]>([])
function fetchIndentityTags() {
  fetchUserTags().then(res => {
    identityTags.value = res.identityTagResponses
  })
}

const showNotificationCell = ref(false)

onInit(() => {
  fetchIndentityTags()
  checkNotificationAvaiable().then(avaiable => {
    showNotificationCell.value = avaiable
  })
})

function onChangeIdentityTag() {
  router.push({
    name: 'identity-onboarding',
    query: {
      backAfterEnd: 'true',
    },
  })
}
</script>

<style scoped></style>
